// src/pages/Public/Iletisim/IletisimPage.tsx
import React, { useMemo, useState } from "react";
import { useTheme } from "../../../context/ThemeContext";

/* ---- mini surface helpers (tema arka planına göre tonlar) ---- */
const hexToRgb = (hex: string) => {
  const h = (hex || "#0f1115").replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
const useSurfaces = (theme?: any) => {
  const bg = theme?.backgroundColor || "#0f1115";
  const isDark = luminance(hexToRgb(bg)) < 0.5;
  return {
    bg,
    surface1: isDark ? "rgba(255,255,255,.04)" : "#ffffff",
    divider:  isDark ? "rgba(255,255,255,.12)" : "#e5e7eb",
    textPrimary: theme?.textPrimary ?? (isDark ? "#ffffff" : "#111827"),
    textSecondary: theme?.textSecondary ?? (isDark ? "rgba(255,255,255,.72)" : "#6b7280"),
    accent: theme?.accent ?? "#22c55e",
  };
};

const isEmail = (v: string) => /\S+@\S+\.\S+/.test(v);

const IletisimPage: React.FC = () => {
  const { theme } = useTheme();
  const S = useSurfaces(theme);

  const [ad, setAd] = useState("");
  const [email, setEmail] = useState("");
  const [mesaj, setMesaj] = useState("");
  const [sent, setSent] = useState<null | "ok" | "err">(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const adErr = useMemo(() => touched.ad && !ad.trim() ? "Ad zorunludur." : "", [ad, touched.ad]);
  const emailErr = useMemo(() => touched.email && (email.trim().length === 0 ? "E-posta zorunludur." : (!isEmail(email) ? "Geçerli bir e-posta girin." : "")), [email, touched.email]);
  const msgErr = useMemo(() => touched.mesaj && mesaj.trim().length < 10 ? "Mesaj en az 10 karakter olmalı." : "", [mesaj, touched.mesaj]);

  const submitDisabled = !!(adErr || emailErr || msgErr) || !ad || !email || !mesaj;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ ad: true, email: true, mesaj: true });
    if (submitDisabled) return;
    // Burada gerçek API çağrınızı yapabilirsiniz.
    // Bu örnekte sadece sahte bir “başarılı” durumu gösteriyoruz.
    try {
      // await postRequest(...);
      setSent("ok");
      setAd(""); setEmail(""); setMesaj("");
      setTouched({});
      setTimeout(() => setSent(null), 3000);
    } catch {
      setSent("err");
      setTimeout(() => setSent(null), 3500);
    }
  };

  const inputBase =
    "w-full rounded px-3 py-2 bg-transparent outline-none transition border";
  const inputStyle = (hasErr: boolean) => ({
    borderColor: hasErr ? "#ef4444" : S.divider,
    color: S.textPrimary as string,
  } as React.CSSProperties);

  return (
    <div
      className="min-h-[70vh] px-4 py-10"
      style={{ backgroundColor: S.bg }}
    >
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-normal " style={{ color: S.textPrimary }}>
          İletişim
        </h1>

        <div
          className="mt-6 rounded-2xl p-5 border shadow-sm"
          style={{ background: S.surface1, borderColor: S.divider }}
        >
          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <div>
              <label className="block text-sm mb-1" style={{ color: S.textSecondary }}>
                Adınız <span className="text-red-500">*</span>
              </label>
              <input
                className={inputBase}
                style={inputStyle(!!adErr)}
                placeholder="Ad Soyad"
                value={ad}
                onChange={(e) => setAd(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, ad: true }))}
              />
              {adErr && <p className="mt-1 text-xs text-red-600">{adErr}</p>}
            </div>

            <div>
              <label className="block text-sm mb-1" style={{ color: S.textSecondary }}>
                E-posta <span className="text-red-500">*</span>
              </label>
              <input
                className={inputBase}
                style={inputStyle(!!emailErr)}
                placeholder="ornek@site.com"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, email: true }))}
              />
              {emailErr && <p className="mt-1 text-xs text-red-600">{emailErr}</p>}
            </div>

            <div>
              <label className="block text-sm mb-1" style={{ color: S.textSecondary }}>
                Mesajınız <span className="text-red-500">*</span>
              </label>
              <textarea
                className={inputBase}
                style={inputStyle(!!msgErr)}
                placeholder="Mesajınızı yazın…"
                rows={5}
                value={mesaj}
                onChange={(e) => setMesaj(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, mesaj: true }))}
              />
              {msgErr && <p className="mt-1 text-xs text-red-600">{msgErr}</p>}
            </div>

            {sent === "ok" && (
              <div
                className="rounded-md px-3 py-2 text-sm border"
                style={{ background: "rgba(16,185,129,.08)", borderColor: "rgba(16,185,129,.3)", color: S.textPrimary }}
              >
                Teşekkürler! Mesajınız alındı.
              </div>
            )}
            {sent === "err" && (
              <div
                className="rounded-md px-3 py-2 text-sm border text-red-700"
                style={{ background: "rgba(239,68,68,.08)", borderColor: "rgba(239,68,68,.3)" }}
              >
                Mesaj gönderilemedi. Lütfen daha sonra tekrar deneyin.
              </div>
            )}

            <button
              type="submit"
              disabled={submitDisabled}
              className="px-4 py-2 rounded text-white font-medium disabled:opacity-60"
              style={{ background: S.accent }}
            >
              Gönder
            </button>
          </form>

          {/* İsteğe bağlı: iletişim bilgileri bloğu */}
          <div className="mt-6 text-sm" style={{ color: S.textSecondary }}>
            Bize e-posta ile ulaşın:{" "}
            <a href="mailto:info@ornek.com" style={{ color: S.accent }}>
              info@ornek.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IletisimPage;
